import ApplicationController from '../../application_controller.js';
import { Janus } from 'janus-gateway';

// WebRTC Quality Controller Functions

class WebRTCStatsHistory {

    constructor() {
      this.history = [];
      this.maxEntries = 10;
    }
  
    add(statsReport) {
      this.history.push(statsReport);
      if (this.history.length > this.maxEntries) {
        this.history.shift();
      }
    }
  
    getLast60Stats() {
      return this.history;
    }
  
    getAverage(key) {
      let sum = 0;
      let count = 0;
      for (let statsReport of this.history) {
        if (key in statsReport) {
          sum += statsReport[key];
          count++;
        }
      }
      return count > 0 ? sum / count : null;
    }
  
    getDifference(key) {
      //returns the difference between the earliest and latest key
      if (this.history.length === 0) {
        return null;
      }
      let earliestValue = this.history[0][key];
      let latestValue = this.history[this.history.length - 1][key];
      return latestValue - earliestValue;
    }
  
    getLatest(key) {
      if (this.history.length === 0) {
        return null;
      }
      let latestValue = this.history[this.history.length - 1][key];
      return latestValue;
    }
  
    stats() {
      return {
        averageUpstreamPacketLoss: (this.getDifference('packetsLostUpstream')/this.getDifference('packetsSent')),
        averageDownstreamPacketLoss: (this.getDifference('packetsLostDownstream')/(this.getDifference('packetsReceivedDownstream')+this.getDifference('packetsLostDownstream'))),
        averageJitter: this.getAverage('jitter'),
        averageRTT: this.getAverage('roundTripTime'),
        currentFrameRateOut: this.getLatest('frameRateOutbound'),
        currentTargetBitRate: this.getLatest('targetBitrate'),
        currentFrameWidth: this.getLatest('frameWidth'),
        currentFrameHeight: this.getLatest('frameHeight'),
        currentAvailableBitrate: this.getLatest('availableOutgoingBitrate')
      }
    }
}




export default class QualityController extends ApplicationController {

    monitorAudioStats(pluginHandle) {
        // Clear any existing interval, so we're only collecting once
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
            this.statsInterval = null;
        }
    
        const audioStatsHistory = new WebRTCStatsHistory();
        if (pluginHandle && pluginHandle.webrtcStuff) {
            this.audioPeerConnection = pluginHandle.webrtcStuff.pc;    
        } else {
            console.warn('No active PeerConnection to monitor.');
            return;
        }
      
        const stateDefinitions = [
            { name: 'terribleDownload', check: stats => stats.averageDownstreamPacketLoss > 0.15 || stats.averageRTT > 500 },
            { name: 'terribleUpload', check: stats => stats.averageUpstreamPacketLoss > 0.15 || stats.averageRTT > 500 },
            { name: 'poorDownload', check: stats => stats.averageDownstreamPacketLoss > 0.10 || stats.averageRTT > 300 },
            { name: 'poorUpload', check: stats => stats.averageUpstreamPacketLoss > 0.05 || stats.averageRTT > 300 },
            { name: 'connectionRestored', check: stats => stats.averageUpstreamPacketLoss < 0.03 && stats.averageDownstreamPacketLoss < 0.03 && stats.averageRTT <= 300 }
        ];
    
        let currentState = 'connectionRestored';
    
        this.statsInterval = setInterval(async () => {
            const audioConnectionStats = await this.getPeerConnectionStats(this.audioPeerConnection);
            audioStatsHistory.add(audioConnectionStats);
            let currentStats = audioStatsHistory.stats();
            
            //console.log(currentStats);
    
            // Determine new state
            for (const { name, check } of stateDefinitions) {
                if (check(currentStats)) {
                    if (name !== currentState) {
                        document.dispatchEvent(new CustomEvent('audioEvent', { 
                            detail: { type: name, stats: currentStats }
                        }));
                        currentState = name;
                    }
                    break; // Exit the loop once we find a matching state
                }
            }
        }, 1000);
    }

    async getPeerConnectionStats(peerConnection) {
        // collect and return the important webRTC stats
        if (!(peerConnection instanceof RTCPeerConnection)) {
          throw new Error('Input is not a valid RTCPeerConnection');
        }
    
        const statsReport = await peerConnection.getStats();  
        const extractedStats = {};
        let packetsSent = 0;
    
        statsReport.forEach(stat => {
          switch (stat.type) {
            case 'inbound-rtp':
              const inboundPacketLossPercentage = 
                  (stat.packetsLost / (stat.packetsLost + stat.packetsReceived)) * 100;
              
              extractedStats.packetsReceivedDownstream = stat.packetsReceived;
              extractedStats.packetsLostDownstream = stat.packetsLost;
              extractedStats.packetLossPercentageDownstream = inboundPacketLossPercentage.toFixed(2) + "%";
              extractedStats.jitter = stat.jitter;
              extractedStats.bytesReceivedDownstream = stat.bytesReceived;
              extractedStats.frameRateInbound = stat.framerateMean;
              break;
        
            case 'outbound-rtp':
              if (stat.kind == 'video' && stat.rid == 'm') {
                packetsSent = stat.packetsSent;
                extractedStats.packetsSent = stat.packetsSent;
                extractedStats.bytesSent = stat.bytesSent;
                extractedStats.frameRateOutbound = stat.framesPerSecond;
                extractedStats.targetBitrate = stat.targetBitrate;
                extractedStats.frameWidth = stat.frameWidth;
                extractedStats.frameHeight = stat.frameHeight;
    
              }else if (stat.kind == 'video' && stat.rid == 'l') {
                packetsSent = stat.packetsSent;
                extractedStats.packetsSentLowRes = stat.packetsSent;
                extractedStats.bytesSentLowRes = stat.bytesSent;
                extractedStats.frameRateOutboundLowRes = stat.framesPerSecond;
                extractedStats.targetBitrateLowRes = stat.targetBitrate;
                extractedStats.frameWidthLowRes = stat.frameWidth;
                extractedStats.frameHeightLowRes = stat.frameHeight;
    
              } else if (stat.kind == 'audio') {
                packetsSent = stat.packetsSent;
                extractedStats.packetsSent = stat.packetsSent;
                extractedStats.bytesSent = stat.bytesSent;
              }
    
              break;
        
            case 'candidate-pair':
              extractedStats.localCandidateType = stat.localCandidateType;
              extractedStats.remoteCandidateType = stat.remoteCandidateType;
              extractedStats.state = stat.state;
              extractedStats.roundTripTime = stat.currentRoundTripTime;
              extractedStats.availableOutgoingBitrate = stat.availableOutgoingBitrate;
              break;
        
            case 'remote-inbound-rtp':
              const remoteInboundPacketLossPercentage = 
                  (stat.packetsLost /  packetsSent) * 100;
        
              extractedStats.packetsReceivedUpstream = stat.packetsReceived;
              extractedStats.packetsLostUpstream = stat.packetsLost;
              extractedStats.packetLossPercentageUpstream = remoteInboundPacketLossPercentage.toFixed(2) + "%";
              extractedStats.roundTripTimeRemote = stat.roundTripTime;
              break;
        
            case 'track':
              extractedStats.trackIdentifier = stat.trackIdentifier;
              extractedStats.framesSent = stat.framesSent;
              extractedStats.framesReceived = stat.framesReceived;
              break;
          }
        });
      
    
        return extractedStats;
      }

    //-------------------------------------------------------------------------
    // Video publish diagnostics.
    // Only ever called from the video failure recovery path in
    // webrtc_controller.js. Nothing here runs for a successful publish.
    //
    // Deliberately separate from getPeerConnectionStats above, which runs on a
    // 1s interval for every working client and throws on a non-PeerConnection.
    //-------------------------------------------------------------------------

    // Resolves to the Rollbar payload. Never throws, never rejects.
    async captureVideoPublishDiagnostics({ pluginHandle, localStream, error, stage, sampleGapMs = 1500 }) {
        try {
            const webrtcStuff = (pluginHandle && pluginHandle.webrtcStuff) || {};
            const peerConnection = webrtcStuff.pc || null;

            const sample1 = await this.sampleVideoSendStats(peerConnection);

            // Did both simulcast layers survive into the encoder?
            let senders = [];
            if (peerConnection && typeof peerConnection.getSenders === 'function') {
                senders = peerConnection.getSenders()
                    .filter((sender) => sender.track && sender.track.kind === 'video')
                    .map((sender) => {
                        const parameters = sender.getParameters() || {};
                        return {
                            encodings: (parameters.encodings || []).map((encoding) => ({
                                rid: encoding.rid,
                                active: encoding.active,
                                maxBitrate: encoding.maxBitrate,
                                maxFramerate: encoding.maxFramerate,
                                scaleResolutionDownBy: encoding.scaleResolutionDownBy,
                                scalabilityMode: encoding.scalabilityMode
                            })),
                            trackReadyState: sender.track.readyState,
                            trackMuted: sender.track.muted
                        };
                    });
            }

            const videoTrack = localStream && localStream.getVideoTracks
                ? localStream.getVideoTracks()[0]
                : null;
            const trackInfo = videoTrack ? {
                readyState: videoTrack.readyState,
                muted: videoTrack.muted,
                enabled: videoTrack.enabled,
                label: videoTrack.label,
                settings: videoTrack.getSettings ? videoTrack.getSettings() : null
            } : null;
            if (trackInfo && trackInfo.settings) {
                // Not useful, and identifies the device.
                delete trackInfo.settings.deviceId;
                delete trackInfo.settings.groupId;
            }

            let h264Capabilities = null;
            try {
                const capabilities = (typeof RTCRtpSender !== 'undefined' && RTCRtpSender.getCapabilities)
                    ? RTCRtpSender.getCapabilities('video')
                    : null;
                if (capabilities && capabilities.codecs) {
                    h264Capabilities = capabilities.codecs
                        .filter((codec) => /h264/i.test(codec.mimeType || ''))
                        .map((codec) => codec.sdpFmtpLine);
                }
            } catch (capabilitiesError) {
                h264Capabilities = 'unavailable';
            }

            await new Promise((resolve) => setTimeout(resolve, sampleGapMs));
            const sample2 = await this.sampleVideoSendStats(peerConnection);

            const classification = this.classifyPublishFailure(sample1, sample2, trackInfo);

            return {
                stage: stage,
                verdict: classification.verdict,
                deltas: classification.deltas,
                errorMessage: String((error && error.message) || error),
                errorName: error && error.name,
                ua: navigator.userAgent,
                screen: { width: window.screen.width, height: window.screen.height, dpr: window.devicePixelRatio },
                hadPeerConnection: !!peerConnection,
                pcState: peerConnection ? {
                    connectionState: peerConnection.connectionState,
                    iceConnectionState: peerConnection.iceConnectionState,
                    signalingState: peerConnection.signalingState
                } : null,
                trackInfo: trackInfo,
                senders: senders,
                localSdp: this.extractVideoSdpLines(webrtcStuff.mySdp && webrtcStuff.mySdp.sdp),
                remoteSdp: this.extractVideoSdpLines(webrtcStuff.remoteSdp),
                h264Capabilities: h264Capabilities,
                sample1: sample1,
                sample2: sample2
            };
        } catch (diagnosticsError) {
            return { stage: stage, verdict: 'diagnostics_failed', diagnosticsError: String(diagnosticsError) };
        }
    }

    // Point in time read of the video send stats. Returns null when there is no
    // usable PeerConnection - unlike getPeerConnectionStats, this never throws.
    async sampleVideoSendStats(peerConnection) {
        if (!(peerConnection instanceof RTCPeerConnection)) {
            return null;
        }

        const sample = { tMs: Date.now(), mediaSource: null, outboundByRid: {}, codecs: {}, candidatePair: null };

        try {
            const statsReport = await peerConnection.getStats();

            statsReport.forEach((stat) => {
                if (stat.type === 'media-source' && stat.kind === 'video') {
                    sample.mediaSource = {
                        frames: stat.frames,
                        framesPerSecond: stat.framesPerSecond,
                        width: stat.width,
                        height: stat.height
                    };
                } else if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
                    // Keyed per rid: an aggregate would hide one simulcast layer
                    // encoding while the other is dead, which is the whole point.
                    sample.outboundByRid[stat.rid || stat.mid || 'default'] = {
                        rid: stat.rid,
                        mid: stat.mid,
                        active: stat.active,
                        framesEncoded: stat.framesEncoded,
                        framesSent: stat.framesSent,
                        framesPerSecond: stat.framesPerSecond,
                        keyFramesEncoded: stat.keyFramesEncoded,
                        frameWidth: stat.frameWidth,
                        frameHeight: stat.frameHeight,
                        bytesSent: stat.bytesSent,
                        packetsSent: stat.packetsSent,
                        totalEncodeTime: stat.totalEncodeTime,
                        qualityLimitationReason: stat.qualityLimitationReason,
                        encoderImplementation: stat.encoderImplementation,
                        powerEfficientEncoder: stat.powerEfficientEncoder,
                        scalabilityMode: stat.scalabilityMode,
                        targetBitrate: stat.targetBitrate,
                        codecId: stat.codecId
                    };
                } else if (stat.type === 'codec') {
                    sample.codecs[stat.id] = {
                        mimeType: stat.mimeType,
                        payloadType: stat.payloadType,
                        sdpFmtpLine: stat.sdpFmtpLine
                    };
                } else if (stat.type === 'candidate-pair' && stat.nominated) {
                    sample.candidatePair = {
                        state: stat.state,
                        currentRoundTripTime: stat.currentRoundTripTime,
                        availableOutgoingBitrate: stat.availableOutgoingBitrate,
                        bytesSent: stat.bytesSent
                    };
                }
            });
        } catch (statsError) {
            return null;
        }

        return sample;
    }

    // Pulls the interesting lines out of the video m-section. Rollbar truncates
    // telemetry messages, so we send these rather than a whole SDP.
    extractVideoSdpLines(sdp) {
        if (!sdp || typeof sdp !== 'string') {
            return null;
        }

        const sections = sdp.split(/\r?\nm=/);
        const videoSection = sections.find((section, index) => index > 0 && section.startsWith('video'));
        if (!videoSection) {
            return null;
        }

        const lines = ('m=' + videoSection).split(/\r?\n/);
        const linesStartingWith = (prefix) => lines.filter((line) => line.indexOf(prefix) === 0);

        return {
            mLine: lines[0],
            rid: linesStartingWith('a=rid:'),
            simulcast: linesStartingWith('a=simulcast:'),
            fmtp: linesStartingWith('a=fmtp:'),
            rtpmap: linesStartingWith('a=rtpmap:'),
            direction: lines.find((line) => /^a=(sendrecv|sendonly|recvonly|inactive)$/.test(line))
        };
    }

    // Separates "the camera produced nothing" from "the encoder produced
    // nothing" - the two families this diagnostic exists to tell apart.
    classifyPublishFailure(sample1, sample2, trackInfo) {
        const deltas = {};

        if (!trackInfo || trackInfo.readyState !== 'live') {
            return { verdict: 'track_not_live', deltas: deltas };
        }
        if (trackInfo.muted === true) {
            return { verdict: 'camera_muted', deltas: deltas };
        }
        if (!sample2) {
            return { verdict: 'no_peer_connection', deltas: deltas };
        }

        const firstSample = sample1 || { mediaSource: null, outboundByRid: {} };

        if (!sample2.mediaSource && !firstSample.mediaSource) {
            return { verdict: 'no_media_source_stats', deltas: deltas };
        }

        const since = (later, earlier) => (later || 0) - (earlier || 0);

        deltas.frames = since(
            sample2.mediaSource && sample2.mediaSource.frames,
            firstSample.mediaSource && firstSample.mediaSource.frames
        );
        if (deltas.frames <= 0) {
            return { verdict: 'camera_no_frames', deltas: deltas };
        }

        const rids = Object.keys(sample2.outboundByRid);
        deltas.encodedByRid = {};
        let totalEncoded = 0;
        let totalSent = 0;
        let ridsEncoding = 0;

        rids.forEach((rid) => {
            const later = sample2.outboundByRid[rid] || {};
            const earlier = firstSample.outboundByRid[rid] || {};
            const encoded = since(later.framesEncoded, earlier.framesEncoded);
            // framesSent, never packetsSent: Chromium sends RTP padding for
            // bandwidth probing, so packetsSent climbs on a dead encoder.
            const sent = since(later.framesSent, earlier.framesSent);
            deltas.encodedByRid[rid] = { framesEncoded: encoded, framesSent: sent };
            totalEncoded += encoded;
            totalSent += sent;
            if (encoded > 0) { ridsEncoding += 1; }
        });

        deltas.totalFramesEncoded = totalEncoded;
        deltas.totalFramesSent = totalSent;

        if (totalEncoded <= 0) {
            return { verdict: 'encoder_stall', deltas: deltas };
        }
        if (rids.length > 1 && ridsEncoding < rids.length) {
            return { verdict: 'partial_simulcast_encode', deltas: deltas };
        }
        if (totalSent <= 0) {
            return { verdict: 'encoded_not_sent', deltas: deltas };
        }

        // Frames are encoding and leaving the device - we simply gave up early.
        return { verdict: 'late_publish', deltas: deltas };
    }
}